import { Award, ExternalLink, Lightbulb } from "lucide-react";
import type { CSSProperties } from "react";

import { cn } from "~/lib/cn";
import { deriveCoachStatuses } from "~/lib/coach-status";
import { Feature, hasFeature } from "~/lib/has-feature";
import type { MembershipPlan } from "~/lib/schemas/db-enums";
import { getAppUrl } from "~/lib/utils";

import { CoachCardLock } from "./coach-card-lock";
import { ButtonLink } from "./ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { CDNImage } from "./ui/cdn-image";
import { CoachProfileImage } from "./ui/coach-profile-image";
import { CoachStatusBadge } from "./ui/coach-status-badge";
import { HighlightBorder, deriveHighlightType } from "./ui/highlight-border";

// Minimal props interface for CoachCard - only defines fields actually used
export interface CoachCardMinimalData {
  coach: {
    id: string;
    username: string;
    fullName: string;
    membershipPlan: MembershipPlan;
    profilePictureUrl?: string | null;
    isFeaturedCoach?: boolean | null;
    isFoundingCoach?: boolean | null;
  };
  profile: {
    coachingTitle?: string | null;
    brandLogoUrl?: string | null;
    brandName?: string | null;
    title?: string | null;
  } | null;
  similarity?: number;
  expertise?: string;
  /** Average of engagement-verified client ratings (1–5), if any. */
  ratingAverage?: number | null;
  ratingCount?: number;
}

export interface CoachCardProps {
  coachData: CoachCardMinimalData;
  isActive?: boolean;
  isEnabled?: boolean;
  style?: CSSProperties;
  className?: string;
  href?: string;
  /** Appended as ?ref={source} on all profile links for source attribution */
  source?: string;
  onLockClick?: () => void;
}

export const CoachCard = ({
  coachData,
  isActive = false,
  isEnabled = true,
  style,
  className,
  ...props
}: CoachCardProps) => {
  const baseHref = props.href ?? `/${coachData.coach.username}`;
  const href = (() => {
    if (!props.source) return baseHref;
    const url = new URL(baseHref, getAppUrl());
    url.searchParams.set("ref", props.source);
    return url.pathname + url.search;
  })();
  const statusBadges = deriveCoachStatuses({
    membershipPlan: coachData.coach.membershipPlan,
    isFeaturedCoach: coachData.coach.isFeaturedCoach,
    isFoundingCoach: coachData.coach.isFoundingCoach,
  });
  const highlightType = isEnabled ? deriveHighlightType(coachData.coach) : null;

  return (
    <HighlightBorder type={highlightType} className={className} style={style}>
      <Card
        variant="flat"
        key={coachData.coach.id}
        className={cn(
          "relative flex h-80 cursor-pointer flex-col justify-between rounded-2xl",
          {
            "border-primary": isActive,
            "bg-transparent": highlightType === "founding",
          },
        )}
      >
        <a
          className={cn("flex min-h-0 flex-1 flex-col", {
            "blur-sm": !isEnabled,
          })}
          href={isEnabled ? href : ""}
          aria-disabled={!isEnabled}
        >
          <CardHeader className="mb-4">
            <div className="flex gap-4">
              <div className="relative h-20 w-20 flex-shrink-0">
                <CoachProfileImage
                  coach={coachData.coach}
                  profilePictureUrl={coachData.coach.profilePictureUrl}
                  fullName={coachData.coach.fullName}
                  score={
                    coachData.similarity
                      ? Math.round(coachData.similarity * 100)
                      : undefined
                  }
                  scoreBadgeSize="sm"
                />
                {statusBadges.length > 0 && (
                  <div className="absolute -top-1 right-0 flex flex-col items-end gap-1">
                    {statusBadges.map((status, index) => (
                      <CoachStatusBadge
                        key={status}
                        status={status}
                        size="sm"
                        showTooltip
                        className={cn(
                          "shadow-sm",
                          index === 1 && "translate-x-2",
                          index === 2 && "translate-x-3",
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                <CardTitle className="truncate text-2xl leading-tight">
                  {coachData.coach.fullName}
                </CardTitle>
                {coachData.profile?.coachingTitle && (
                  <p className="truncate text-sm leading-snug text-gray-500">
                    {coachData.profile.coachingTitle}
                  </p>
                )}
                {coachData.ratingAverage != null &&
                  (coachData.ratingCount ?? 0) > 0 && (
                    <p className="flex items-center gap-1 text-sm leading-snug">
                      <span aria-hidden className="text-amber-400">
                        ★
                      </span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {coachData.ratingAverage.toFixed(1)}
                      </span>
                      <span className="text-gray-500">
                        ({coachData.ratingCount})
                      </span>
                    </p>
                  )}
                {coachData.profile &&
                  hasFeature(coachData.coach, Feature.FancyProfile) &&
                  coachData.profile.brandLogoUrl && (
                    <div className="mt-1.5 flex w-full items-center justify-start gap-x-2">
                      <CDNImage
                        src={coachData.profile.brandLogoUrl}
                        width={32}
                        height={32}
                        alt={`${coachData.profile.brandName || coachData.coach.fullName}'s brand logo`}
                        className="aspect-square size-8 shrink-0 grow-0 rounded-full border border-gray-200 object-cover"
                      />
                      {coachData.profile.brandName && (
                        <div className="text-text-secondary truncate text-sm font-medium">
                          {coachData.profile.brandName}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="mb-4 flex min-h-0 flex-1 flex-col gap-2.5">
            {coachData.expertise && (
              <div className="flex min-w-0 items-start gap-2">
                <Award className="mt-0.5 size-5 flex-shrink-0 text-brand-700" />
                <div className="text-text-secondary flex-1 truncate text-sm leading-relaxed">
                  {coachData.expertise}
                </div>
              </div>
            )}
            {coachData.profile?.title && (
              <div className="flex min-w-0 items-start gap-2">
                <Lightbulb className="mt-0.5 size-5 flex-shrink-0 text-brand-700" />
                <p className="text-text-secondary line-clamp-3 flex-1 text-sm leading-relaxed">
                  {coachData.profile.title}
                </p>
              </div>
            )}
          </CardContent>
        </a>
        <CardFooter className="flex-shrink-0">
          <ButtonLink
            href={isEnabled ? href : ""}
            key={coachData.coach.id}
            target="_blank"
            className="w-full shrink-0 items-center gap-2 rounded-md"
            aria-disabled={!isEnabled}
          >
            View Profile <ExternalLink />
          </ButtonLink>
        </CardFooter>
        {!isEnabled && <CoachCardLock onClick={props.onLockClick} />}
      </Card>
    </HighlightBorder>
  );
};
